import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { invalidateCaseQueries } from "@/utils/cacheUtils.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type SaveAttorneyFeeSettingsParams = {
    caseUUID: string;
    attorneyFeePercentage?: number | string | null;
    attorneyFeeManualOverride?: number | string | null;
    clientDistributionManualOverride?: number | string | null;
};

export const useSaveAttorneyFeeSettings = (): UseMutationResult<
    void,
    Error,
    SaveAttorneyFeeSettingsParams
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            caseUUID,
            attorneyFeePercentage,
            attorneyFeeManualOverride,
            clientDistributionManualOverride,
        }) => {
            const response = await fetch(
                apiUrl(`/negotiation/save-attorney-settings/${caseUUID}`),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/vnd.api+json",
                    },
                    body: JSON.stringify({
                        data: {
                            id: caseUUID,
                            type: "case",
                            attributes: {
                                attorneyFeePercentage,
                                attorneyFeeManualOverride,
                                clientDistributionManualOverride,
                            },
                        },
                    }),
                },
            );
            await handleJsonApiError(response);
        },
        onSuccess: async (_data, { caseUUID }) => {
            await invalidateCaseQueries(queryClient, caseUUID);
        },
    });
};
